"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useCheckout } from './CheckoutContext';

interface OrderSidebarContextType {
  sidebarOpen: boolean;
  currentOrderId: string | null;
  openSidebar: (orderId: string) => void;
  openSidebarFromCheckout: (orderId: string) => void;
  closeSidebar: () => void;
  fromCheckout: boolean;
  productSidebarOpenedFromOrder: boolean;
  setProductSidebarOpenedFromOrder: (opened: boolean) => void;
}

const OrderSidebarContext = createContext<OrderSidebarContextType | undefined>(undefined);

export const OrderSidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [fromCheckout, setFromCheckout] = useState(false);
  const [productSidebarOpenedFromOrder, setProductSidebarOpenedFromOrder] = useState(false);

  const openSidebar = (orderId: string) => {
    // Forzar recálculo del header inmediatamente antes de abrir
    window.dispatchEvent(new Event('recalculate-header'));
    requestAnimationFrame(() => {
      setCurrentOrderId(orderId);
      setSidebarOpen(true);
    });
  };

  const openSidebarFromCheckout = (orderId: string) => {
    // Cerrar la checkout sidebar antes de abrir la de pedido
    window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'order' } }));
    // Forzar recálculo del header inmediatamente antes de abrir
    window.dispatchEvent(new Event('recalculate-header'));
    setTimeout(() => {
      setCurrentOrderId(orderId);
      setSidebarOpen(true);
      setFromCheckout(true);
    }, 50);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setTimeout(() => {
      setCurrentOrderId(null);
      setFromCheckout(false);
    }, 300);
  };

  // Escuchar evento global para abrir desde toasts (ej. NotificationContext)
  useEffect(() => {
    const handler = (e: Event) => {
      const orderId = (e as CustomEvent<{ orderId: string }>).detail?.orderId;
      if (orderId) openSidebar(orderId);
    };
    window.addEventListener('open-order-sidebar', handler);
    return () => window.removeEventListener('open-order-sidebar', handler);
  }, []);

  return (
    <OrderSidebarContext.Provider value={{ sidebarOpen, currentOrderId, openSidebar, openSidebarFromCheckout, closeSidebar, fromCheckout, productSidebarOpenedFromOrder, setProductSidebarOpenedFromOrder }}>
      {children}
    </OrderSidebarContext.Provider>
  );
};

export const useOrderSidebar = () => {
  const context = useContext(OrderSidebarContext);
  if (!context) {
    throw new Error('useOrderSidebar must be used within OrderSidebarProvider');
  }
  return context;
};
